/**
 * Spec 109 — PersonaDiagnosisDetailPage. The full pitch-grade deliverable for one
 * diagnosis: outcome -> cause -> fix, combined confidence + limiting factor
 * prominent. Admins can re-trigger synthesis (the deliverable-refresh path, FR-13).
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { useAuth } from '../lib/auth-context.js';
import {
  getPersonaDiagnosis,
  synthesizePersonaDiagnosis,
} from '../api/personaDiagnoses.js';
import type { PersonaDiagnosisFull } from '../api/personaDiagnoses.js';
import { PersonaDiagnosisDeliverable } from '../components/PersonaDiagnosisDeliverable.js';

export function PersonaDiagnosisDetailPage() {
  const { selectedBrand } = useBrand();
  const { diagnosisId } = useParams<{ diagnosisId: string }>();
  const brandId = selectedBrand?.id ?? null;

  const { hasRole } = useAuth();
  const isAdmin = hasRole('rule_admin') || hasRole('orbit:admin');

  const [diagnosis, setDiagnosis] = useState<PersonaDiagnosisFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [synthesizing, setSynthesizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!brandId || !diagnosisId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await getPersonaDiagnosis(brandId, diagnosisId);
      setDiagnosis(d);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load diagnosis');
    } finally {
      setLoading(false);
    }
  }, [brandId, diagnosisId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onSynthesize = useCallback(async () => {
    if (!brandId || !diagnosisId) return;
    setSynthesizing(true);
    setMessage(null);
    setError(null);
    try {
      const res = await synthesizePersonaDiagnosis(brandId, diagnosisId);
      setMessage(`Synthesized · ${res.recommendation_count} recommendation(s)`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Synthesis failed');
    } finally {
      setSynthesizing(false);
    }
  }, [brandId, diagnosisId, refresh]);

  return (
    <div className="persona-diagnosis-detail-page hub-page--reading">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          flexWrap: 'wrap',
          marginBottom: 12,
        }}
      >
        <div>
          <h1 className="hub-page__title">Cross-lens persona diagnosis</h1>
          <p style={{ fontSize: 13, color: 'var(--hub-color-ink-muted)', margin: 0 }}>
            The pitch deliverable: outcome → cause → fix, quote-backed and
            confidence-capped. Built outside-in from public engines + public pages.
          </p>
        </div>
        {isAdmin && (
          <button
            data-testid="diagnosis-synthesize-button"
            onClick={() => void onSynthesize()}
            disabled={synthesizing}
            style={{
              background: 'var(--hub-color-action)',
              color: 'var(--hub-color-ink-on-action)',
              border: 'none',
              borderRadius: 6,
              padding: '8px 14px',
              fontWeight: 600,
              cursor: synthesizing ? 'default' : 'pointer',
              opacity: synthesizing ? 0.6 : 1,
            }}
          >
            {synthesizing ? 'Synthesizing…' : 'Re-synthesize deliverable'}
          </button>
        )}
      </div>

      {!brandId && <p style={{ color: 'var(--hub-color-ink-subtle)' }}>Select a brand to view a diagnosis.</p>}
      {loading && <p style={{ color: 'var(--hub-color-ink-subtle)' }}>Loading…</p>}
      {message && (
        <p data-testid="diagnosis-synthesize-message" style={{ color: 'var(--hub-color-success)' }}>
          {message}
        </p>
      )}
      {error && (
        <p data-testid="diagnosis-error" style={{ color: 'var(--hub-color-danger)' }}>
          {error}
        </p>
      )}
      {diagnosis && <PersonaDiagnosisDeliverable diagnosis={diagnosis} />}
    </div>
  );
}
